<template>
  <div :class="[
    $style.app,
    `current-tab-${currentTab}`,
    `previous-tab-${previousTab}`
  ]">

    <!-- Loading overlay -->
    <transition name="loading">
      <Loading v-if="cards.loading" />
    </transition>

    <!-- Top bar -->
    <div :class="$style.topbar">
      <a href="/" :class="$style.sitraLogo">
        <img src="../assets/images/logo-sitra.svg">
      </a>
      <a
        href="/megatrendikortit"
        :class="[
          $style.megatrendiLogo,
          currentTab === 'table' && !cardsOnTable.length && $style.hide
        ]"
      >
        <span v-html="translations[currentLanguage].startScreenHtmlTitle"></span>
      </a>
      <div :class="$style.languageSelector">
        <img
          @click="languageSelectorOpen = !languageSelectorOpen"
          src="../assets/images/icon-globe.svg"
        />
        <div
          :class="[
            $style.languages,
            languageSelectorOpen && $style.open
          ]
        ">
          <a
            v-for="lang in locale"
            :key="lang"
            href="#"
            @click="switchLanguage(lang)"
          >
            {{lang}}
          </a>
        </div>
      </div>
    </div>

    <!-- Finished loading -->
    <template v-if="!cards.loading">

      <!-- Table screen -->
      <transition name="start-screen">
        <div
          v-if="currentTab === 'table' && !cardsOnTable.length"
          :class="$style.startScreen"
        >
          <div :class="$style.startContainer">
            <div :class="$style.innerContainer">
              <h1 :class="$style.startScreenTitle"><span v-html="translations[currentLanguage].startScreenHtmlTitle"></span></h1>
              <p>{{translations[currentLanguage].startScreenDescription}}</p>
            </div>
            <button
              :class="$style.openCardButton"
              @click="openRandomCard()"
            >
              {{translations[currentLanguage].openCardLabel}}
            </button>
          </div>
        </div>
      </transition>

      <transition name="cards-screen">
        <div
          v-if="currentTab === 'table' && cardsOnTable.length"
          :class="$style.tableScreen"
        >
          <transition-group name="card-list" tag="div" :class="$style.table">
            <div
              :class="$style.item"
              v-for="card in cardsOnTable"
              :key="card.id"
            >
              <Card
                :class="$style.card"
                :title="card.title"
                :description="card.description"
                @close="closeCard(card.id)"
              />
            </div>
            <div
              :class="[$style.item, $style.placeholder, 'placeholder']"
              v-if="!maxCardsOpen"
              :key="`open-card-${cardsOnTable.length}`"
            >
              <Card
                :class="[
                  $style.card,
                  $style.placeholderCard,
                ]"
              >
                <button
                  :class="$style.openCardButton"
                  @click="openRandomCard()"
                >
                  {{translations[currentLanguage].openCardLabel}}
                </button>
              </Card>
            </div>
          </transition-group>
          <p
            v-if="maxCardsOpen"
            :class="$style.maxCardsOpenNotificationText"
          >
            {{translations[currentLanguage].maxAmountOfCardsOnTableText}}
          </p>
        </div>
      </transition>

      <!-- List tab -->
      <transition name="list-screen">
        <div
          v-if="currentTab === 'list'"
          :class="$style.listScreen"
        >
          <ListView
            :cards="cards.data || []"
          />
        </div>
      </transition>

      <!-- Info tab -->
      <transition name="info-screen">
        <div
          v-if="currentTab === 'info'"
          :class="$style.infoScreen"
        >
          <InfoView />
        </div>
      </transition>

      <!-- Navigation bar -->
      <div :class="$style.floatingNavbar">
        <div :class="$style.tabs">

          <button
            :class="[
              $style.tab,
              currentTab === 'table' && $style.active
            ]"
            @click="currentTab = 'table'"
          >
            <img v-if="currentTab === 'table'" src="../assets/images/icon-kortit-white.svg"/>
            <img v-else src="../assets/images/icon-kortit-black.svg"/>
            <p>
              {{translations[currentLanguage].tableTabLabel}}
            </p>
          </button>

          <button
            :class="[
              $style.tab,
              currentTab === 'list' && $style.active
            ]"
            @click="currentTab = 'list'"
          >
            <img v-if="currentTab === 'list'" src="../assets/images/icon-pakka-white.svg"/>
            <img v-else src="../assets/images/icon-pakka-black.svg"/>
            <p>
              {{translations[currentLanguage].listTabLabel}}
            </p>
          </button>

          <button
            :class="[
              $style.tab,
              currentTab === 'info' && $style.active
            ]"
            @click="currentTab = 'info'"
          >
            <img v-if="currentTab === 'info'" src="../assets/images/icon-ohjeet-white.svg"/>
            <img v-else src="../assets/images/icon-ohjeet-black.svg"/>
            <p>
              {{translations[currentLanguage].infoTabLabel}}
            </p>
          </button>
        </div>
      </div>

      <Footer />

    </template>
  </div>
</template>

<script>
import {computed, provide, ref, unref, watch} from '@vue/composition-api'

import useApi from '@/composition/useApi'
import useAsync from '@/composition/useAsync'
import {removeItem, removeRandomItem} from '@/utils/arrays'

export default {
  props: {translations: {type: Object, required: true}},
  setup (props) {
    const api = useApi()
    const cards = useAsync()
    const cardsOnTable = ref([])
    const cardsInDeck = computed(() => {
      if (!cards.data) return
      return cards.data.filter(card => cardsOnTable.value.indexOf(card) === -1)
    })
    const maxCardsOpen = computed(() => {
      return unref(cardsOnTable).length === 4
    })
    const currentTab = ref('table')
    const previousTab = ref()
    watch(currentTab, (newTab, oldTab) => {
      previousTab.value = oldTab

      document.body.classList.remove('current-tab-' + oldTab)
      document.body.classList.add('current-tab-' + newTab)

      scrollToTop()
    }, {
      immediate: true,
    })

    watch(cardsOnTable, (after, before) => {
      if (
        (after.length > 0 && before.length === 0) ||
        (after.length === 0 && before.length > 0)
      ) {
        scrollToTop()
      }
    })

    const languageSelectorOpen = ref(false)
    const currentLanguage = ref(props.translations.initialLanguage)
    const locale = computed(() => {
      return ['sv', 'fi', 'en'].sort((a, b) => a === currentLanguage.value ? -1 : b === currentLanguage ? 1 : 0)
    })

    provide('translations', props.translations)
    provide('currentLanguage', currentLanguage)

    watch(
      currentLanguage,
      () => {
        getCards()
      },
    )

    watch(
      () => cards.data,
      () => {
        translateCardsOnTable()
      },
    )

    getCards()

    return {
      cards,
      cardsOnTable,
      maxCardsOpen,
      currentTab,
      previousTab,
      locale,
      languageSelectorOpen,
      currentLanguage,
      openRandomCard,
      closeCard,
      switchLanguage,
    }

    async function getCards () {
      cards.watch(api.getCards(currentLanguage.value))
    }

    function translateCardsOnTable () {
      const translatedCards = []

      cardsOnTable.value.forEach(card => {
        const translatedCardId = card.translations[currentLanguage.value]
        const translatedCard = cards.data.find(card => card.id === translatedCardId)
        if (translatedCard) translatedCards.push(translatedCard)
      })

      cardsOnTable.value = translatedCards
    }

    function openRandomCard () {
      if (cardsInDeck.value.length === 0) return
      const removeFrom = unref(cardsInDeck)
      const addTo = [...unref(cardsOnTable)]
      addTo.push(removeRandomItem(removeFrom))

      cardsOnTable.value = addTo
    }

    function closeCard (cardId) {
      const removeFrom = [...unref(cardsOnTable)]
      removeItem(removeFrom, removeFrom.find(card => card.id === cardId))

      cardsOnTable.value = removeFrom
    }

    function switchLanguage (lang) {
      currentLanguage.value = lang
    }

    function scrollToTop () {
      window.scrollTo({
        left: 0,
        top: 0,
        behavior: 'smooth',
      })
    }
  },
}
</script>

<style lang="scss">
body {
  transition: background 0.4s ease-in-out;
}

body.current-tab-info {
  background-color: variables.$yellow;
}
</style>

<style scoped lang="scss">
.loading-leave-active {
  transition: opacity 0.5s ease-in-out 0.15s;
}
.loading-leave-to {
  opacity: 0;
}

.card-list-enter-active {
  transition: all 0.5s cubic-bezier(0.33, 1, 0.68, 1);

  &.placeholder {
    transition-delay: 0.125s;
  }
}
.card-list-leave-active {
  transition: all 0.125s cubic-bezier(0.33, 1, 0.68, 1);

  &.placeholder {
    display: none;
  }
}
.card-list-enter {
  opacity: 0;
  transform:
    translateX(-150px)
    translateY(50px)
    rotate(-30deg) !important;

  &.placeholder {
    transform: translateX(-150px) !important;
  }
}
.card-list-leave-to {
  opacity: 0;
  transform: translateX(150px);
}

.list-screen-enter-active,
.list-screen-leave-active,
.info-screen-enter-active,
.info-screen-leave-active,
.start-screen-enter-active,
.start-screen-leave-active,
.cards-screen-enter-active,
.cards-screen-leave-active {
  transition: transform 0.4s ease-in-out;
}
.list-screen-enter-active,
.info-screen-enter-active,
.start-screen-enter-active,
.cards-screen-enter-active {
  z-index: 1;
}
.list-screen-leave-active,
.info-screen-leave-active,
.start-screen-leave-active,
.cards-screen-leave-active {
  position: absolute;
}
.start-screen-leave-to,
.table-screen-leave-to {
  transform: translateX(-100vw);
}
.start-screen-enter,
.table-screen-enter {
  transform: translateX(-100vw);
}
.list-screen-leave-to {
  transform: translateX(100vw);
}
.list-screen-enter {
  transform: translateX(100vw);
}
.previous-tab-info {
  .list-screen-enter {
    transform: translateX(-100vw);
  }
}
.current-tab-info {
  .list-screen-leave-to {
    transform: translateX(-100vw);
  }
}
.info-screen-leave-to {
  transform: translateX(100vw);
}
.info-screen-enter {
  transform: translateX(100vw);
}
.start-screen-leave-to {
  transform: translateY(-100vh);
}
.start-screen-enter {
  transform: translateY(-100vh);
}
.cards-screen-leave-to {
  transform: translateY(100vh);
}
.cards-screen-enter {
  transform: translateY(100vh);
}
</style>

<style module lang="scss">
.app {
  :global {
    @import '~@/style/global';
    @include root-styles;
  }

  padding-bottom: 90px;

  @media screen and (max-width: variables.$breakpoint-700) {
    padding-bottom: 125px;
  }
}
* {
  box-sizing: border-box;
}

.placeholderCard {
  &.card {
    border-style: dashed;
    transform: rotate(0);
  }
  button.openCardButton {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: variables.$yellow;
  }
}

button.openCardButton {
  border-radius: 10px;
  background-color: variables.$yellow;
  padding: variables.$spacing-16 variables.$spacing-24;
}

.closeCardButton {
  cursor: pointer;
  background-color: transparent;
  position: absolute;
  left: 0;
  top: 0;
}

.table {
  display: flex;
  flex-wrap: wrap;
  gap: variables.$spacing-16;
  margin-inline: auto;
  width: 100%;
  max-width: 1140px;
  padding: variables.$spacing-20;
  padding-top: 0;

  .item {
    width: calc(50% - 8px - 2px); // remove gap and border-width to avoid wrapping
    @media screen and (max-width: variables.$breakpoint-700) {
      width: 100%;
    }

    &:not(.placeholder) {
      @include mixins.cardTilt;
    }

    > div {
      height: 100%;
    }
  }
}

.floatingNavbar {
  position: fixed;
  left: 50%;
  bottom: 90px;
  bottom: variables.$spacing-24;
  margin-inline: auto;
  z-index: 2;
  width: 570px;
  max-width: 100%;
  flex-grow: 0;
  transform: translateX(-50%);

  .tabs {
    display: flex;
    gap: variables.$spacing-16;
    justify-content: center;

    .tab {
      display: flex;
      gap: variables.$spacing-16;
      flex-basis: calc(33% - 8px);
      justify-content: center;
      align-items: center;
      border: 1px solid variables.$black;
      background: variables.$white;

      @media screen and (max-width: variables.$breakpoint-700) {
        flex-direction: column;
        width: calc(33% - 22px);
      }

      &.active {
        background: variables.$black;
        color: variables.$white;
      }

      p {
        margin: 0;
        white-space: nowrap;
      }
    }
  }
  button.openCardButton {
    display: block;
    margin-inline: auto;
    margin-bottom: variables.$spacing-24;
  }
}

.infoPageContent {
  position: relative;
  z-index: 1;
  padding: variables.$spacing-20;
  max-width: 670px;
  margin-inline: auto;

}

.maxCardsOpenNotificationText {
  width: 50%;
  text-align: center;
  margin-inline: auto;
  margin-top: variables.$spacing-24 !important;

  @media screen and (max-width: variables.$breakpoint-700) {
    width: 100%;
  }
}

.topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: grid;
  align-items: center;
  grid-template-columns: repeat(3, 1fr);
  padding: variables.$spacing-20;
  z-index: 3;

  .megatrendiLogo {
    margin-inline: auto;
    font-size: 34.5px;
    line-height: 25px;
    color: #000;
    font-weight: 900;
    text-transform: uppercase;
    text-decoration: none;
    text-align: center;

    @media screen and (max-width: variables.$breakpoint-700) {
      font-size: 20px;
      line-height: 14.5px;
    }

    span {
      display: block;
    }
    &.hide {
      opacity: 0;
    }
  }
}

.languageSelector {
  display: flex;
  justify-content: flex-end;
  gap: variables.$spacing-16;

  img {
    cursor: pointer;
    max-width: 30px;
  }

  .languages {
    display: none;
    gap: variables.$spacing-16;

    a {
      font-size: 14px;
      font-weight: 800;
      font-family: variables.$font-family-heading;
      text-transform: uppercase;
      text-decoration: none;
      color: variables.$black;
      margin: auto;

      &:hover {
        text-decoration: underline;
      }
    }

    &.open {
      display: flex;
    }
  }

  @media screen and (max-width: variables.$breakpoint-700) {
    position: relative;
    flex-direction: column;
    align-items: end;
    .languages {
      position: absolute;
      width: 40px;
      right: -5px;
      top: calc(100% + 16px);
      flex-direction: column;
      background: variables.$white;
      padding: 16px 0;
      border-radius: 10px;
    }
  }
}

.listScreen,
.infoScreen,
.startScreen,
.tableScreen {
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  padding-top: 122px;  // desktop navigation height

  @media screen and (max-width: variables.$breakpoint-700) {
    padding-top: 92px; // mobile navigation height
  }
}

.startContainer {
  min-height: calc(100vh - 122px);

  @media screen and (max-width: variables.$breakpoint-700) {
    min-height: calc(100vh - 92px);
  }

  .innerContainer {
    background: variables.$yellow;
    width: 570px;
    max-width: calc(100vw - 40px);
    text-align: center;
    padding: 65px 80px;
    border-radius: variables.$border-radius;
    margin-inline: auto;
    margin-bottom: variables.$spacing-24;

    @media screen and (max-width: variables.$breakpoint-700) {
      padding: 60px 45px;
    }

    p {
      font-family: variables.$font-family-heading;
    }
  }

  .startScreenTitle {
    font-size: 58px;
    line-height: 42px;
    font-weight: 900;
    text-transform: uppercase;
    margin-bottom: variables.$spacing-20;

    @media screen and (max-width: variables.$breakpoint-700) {
      font-size: 41px;
      line-height: 30px;
    }
  }

  button.openCardButton {
    display: block;
    margin-inline: auto;
  }
}
</style>
